import { resolveWorkspaceMediaUrl } from '../lib/urls';
/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Whisper Subtitle Waveform Studio Store (Task 10.5.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export type WhisperModel = 'whisper-large-v3' | 'whisper-medium' | 'whisper-small';
export type SubtitleLanguage = 'auto' | 'vi' | 'en' | 'ja' | 'zh' | 'fr' | 'de' | 'es' | 'ko';
export type SubtitleTask = 'transcribe' | 'translate';
export type SubtitleExportFormat = 'srt' | 'vtt' | 'txt' | 'csv' | 'json';
export type SubtitleStudioTab = 'editor' | 'plain-text' | 'export';

export interface SubtitleWord {
  word: string;
  start: number; // in seconds
  end: number;
  confidence?: number;
}

export interface SubtitleCue {
  id: string;
  index: number; // 1-based index
  start: number; // in seconds
  end: number;
  text: string;
  words?: SubtitleWord[];
  speaker?: string;
  translation?: string;
}

export interface AudioSourceFile {
  name: string;
  size: number;
  type: string;
  url: string;
  durationSec: number;
}

/**
 * Format seconds into SubRip timestamp (HH:MM:SS,mmm)
 */
export function formatTimestampSrt(seconds: number): string {
  const safeSec = Math.max(0, Number(seconds) || 0);
  const totalMs = Math.round(safeSec * 1000);
  const ms = totalMs % 1000;
  const totalSec = Math.floor(totalMs / 1000);
  const s = totalSec % 60;
  const m = Math.floor(totalSec / 60) % 60;
  const h = Math.floor(totalSec / 3600);

  const pad = (n: number, z = 2) => String(n).padStart(z, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)},${pad(ms, 3)}`;
}

/**
 * Format seconds into WebVTT timestamp (HH:MM:SS.mmm)
 */
export function formatTimestampVtt(seconds: number): string {
  const safeSec = Math.max(0, Number(seconds) || 0);
  const totalMs = Math.round(safeSec * 1000);
  const ms = totalMs % 1000;
  const totalSec = Math.floor(totalMs / 1000);
  const s = totalSec % 60;
  const m = Math.floor(totalSec / 60) % 60;
  const h = Math.floor(totalSec / 3600);

  const pad = (n: number, z = 2) => String(n).padStart(z, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(ms, 3)}`;
}

/**
 * Format seconds into MM:SS.ss timecode
 */
export function formatTimecode(seconds: number): string {
  const safeSec = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(safeSec / 60);
  const s = (safeSec % 60).toFixed(2);
  const pad = (v: number | string) => String(v).padStart(2, '0');
  const [secPart, msPart] = s.split('.');
  return `${pad(m)}:${pad(secPart)}.${msPart || '00'}`;
}

/**
 * Parse timestamp string into float seconds
 * Supports HH:MM:SS.mmm, MM:SS.mmm, or plain seconds
 */
export function parseTimestamp(str: string): number {
  if (!str) return 0;
  const cleaned = str.trim().replace(',', '.');
  const parts = cleaned.split(':');

  if (parts.length === 3) {
    const h = parseFloat(parts[0]) || 0;
    const m = parseFloat(parts[1]) || 0;
    const s = parseFloat(parts[2]) || 0;
    return +(h * 3600 + m * 60 + s).toFixed(3);
  } else if (parts.length === 2) {
    const m = parseFloat(parts[0]) || 0;
    const s = parseFloat(parts[1]) || 0;
    return +(m * 60 + s).toFixed(3);
  }
  return +(parseFloat(cleaned) || 0).toFixed(3);
}

/**
 * Exporters for Subtitles
 */
export function generateSrt(cues: SubtitleCue[]): string {
  return cues
    .map((c, i) => {
      const idx = i + 1;
      const start = formatTimestampSrt(c.start);
      const end = formatTimestampSrt(c.end);
      return `${idx}\n${start} --> ${end}\n${c.text.trim()}\n`;
    })
    .join('\n');
}

export function generateVtt(cues: SubtitleCue[]): string {
  const body = cues
    .map((c, i) => {
      const idx = i + 1;
      const start = formatTimestampVtt(c.start);
      const end = formatTimestampVtt(c.end);
      return `${idx}\n${start} --> ${end}\n${c.text.trim()}\n`;
    })
    .join('\n');
  return `WEBVTT\n\n${body}`;
}

export function generateTxt(cues: SubtitleCue[]): string {
  return cues
    .map((c) => c.text.trim())
    .filter(Boolean)
    .join('\n\n');
}

export function generateCsv(cues: SubtitleCue[]): string {
  const header = '\uFEFFIndex,StartTime,EndTime,Duration,Text\n';
  const rows = cues
    .map((c, i) => {
      const idx = i + 1;
      const dur = +(c.end - c.start).toFixed(2);
      const escapedText = `"${c.text.replace(/"/g, '""')}"`;
      return `${idx},${formatTimestampSrt(c.start)},${formatTimestampSrt(c.end)},${dur},${escapedText}`;
    })
    .join('\n');
  return header + rows;
}

export function generateJson(cues: SubtitleCue[], metadata: Record<string, unknown> = {}): string {
  return JSON.stringify(
    {
      format: 'sofia_subtitles_v1',
      generatedAt: new Date().toISOString(),
      metadata,
      cuesCount: cues.length,
      cues: cues.map((c, i) => ({
        index: i + 1,
        id: c.id,
        start: c.start,
        end: c.end,
        duration: +(c.end - c.start).toFixed(3),
        text: c.text,
        speaker: c.speaker,
        words: c.words || [],
      })),
    },
    null,
    2
  );
}

/**
 * Seed Demo Cues for Immediate Audition
 */
export const SAMPLE_AUDIO_FILE: AudioSourceFile = {
  name: 'tech_keynote_keynotes_presentation.mp3',
  size: 3450200, // ~3.3 MB
  type: 'audio/mpeg',
  url: resolveWorkspaceMediaUrl('audio/samples/tech-keynote.mp3'),
  durationSec: 36.5,
};

export const SAMPLE_SUBTITLE_CUES: SubtitleCue[] = [
  {
    id: 'cue-1',
    index: 1,
    start: 0.5,
    end: 4.8,
    text: 'Xin chào quý vị khán giả và cộng đồng công nghệ Việt Nam.',
    speaker: 'Speaker 1',
    words: [
      { word: 'Xin', start: 0.5, end: 0.8 },
      { word: 'chào', start: 0.82, end: 1.15 },
      { word: 'quý', start: 1.2, end: 1.45 },
      { word: 'vị', start: 1.48, end: 1.7 },
      { word: 'khán', start: 1.72, end: 2.1 },
      { word: 'giả', start: 2.12, end: 2.45 },
      { word: 'và', start: 2.6, end: 2.85 },
      { word: 'cộng', start: 2.9, end: 3.2 },
      { word: 'đồng', start: 3.22, end: 3.55 },
      { word: 'công', start: 3.6, end: 3.9 },
      { word: 'nghệ', start: 3.92, end: 4.25 },
      { word: 'Việt', start: 4.3, end: 4.55 },
      { word: 'Nam.', start: 4.58, end: 4.8 },
    ],
  },
  {
    id: 'cue-2',
    index: 2,
    start: 5.2,
    end: 11.4,
    text: 'Hôm nay chúng tôi tự hào giới thiệu nền tảng AI thế hệ mới với kiến trúc Cloudflare Edge siêu tốc.',
    speaker: 'Speaker 1',
    words: [
      { word: 'Hôm', start: 5.2, end: 5.5 },
      { word: 'nay', start: 5.52, end: 5.8 },
      { word: 'chúng', start: 5.85, end: 6.15 },
      { word: 'tôi', start: 6.18, end: 6.45 },
      { word: 'tự', start: 6.5, end: 6.75 },
      { word: 'hào', start: 6.78, end: 7.1 },
      { word: 'giới', start: 7.15, end: 7.45 },
      { word: 'thiệu', start: 7.48, end: 7.8 },
      { word: 'nền', start: 7.85, end: 8.1 },
      { word: 'tảng', start: 8.12, end: 8.4 },
      { word: 'AI', start: 8.45, end: 8.8 },
      { word: 'thế', start: 8.85, end: 9.1 },
      { word: 'hệ', start: 9.12, end: 9.35 },
      { word: 'mới', start: 9.38, end: 9.7 },
      { word: 'với', start: 9.75, end: 10.0 },
      { word: 'kiến', start: 10.02, end: 10.25 },
      { word: 'trúc', start: 10.28, end: 10.55 },
      { word: 'Cloudflare', start: 10.6, end: 11.0 },
      { word: 'Edge.', start: 11.02, end: 11.4 },
    ],
  },
  {
    id: 'cue-3',
    index: 3,
    start: 12.0,
    end: 18.5,
    text: 'Hệ thống bóc băng Whisper STT tích hợp đồng bộ từng mili-giây, giúp tạo phụ đề video tự động chỉ trong vài giây.',
    speaker: 'Speaker 2',
    words: [
      { word: 'Hệ', start: 12.0, end: 12.25 },
      { word: 'thống', start: 12.28, end: 12.6 },
      { word: 'bóc', start: 12.65, end: 12.95 },
      { word: 'băng', start: 13.0, end: 13.3 },
      { word: 'Whisper', start: 13.35, end: 13.8 },
      { word: 'STT', start: 13.85, end: 14.3 },
      { word: 'tích', start: 14.35, end: 14.65 },
      { word: 'hợp', start: 14.68, end: 14.95 },
      { word: 'đồng', start: 15.0, end: 15.3 },
      { word: 'bộ', start: 15.32, end: 15.6 },
      { word: 'từng', start: 15.65, end: 15.95 },
      { word: 'mili-giây.', start: 16.0, end: 16.6 },
    ],
  },
  {
    id: 'cue-4',
    index: 4,
    start: 19.2,
    end: 25.8,
    text: 'Toàn bộ dữ liệu được kết xuất trực tiếp trên trình duyệt của bạn với chi phí truyền tải hoàn toàn bằng không.',
    speaker: 'Speaker 2',
    words: [
      { word: 'Toàn', start: 19.2, end: 19.5 },
      { word: 'bộ', start: 19.52, end: 19.8 },
      { word: 'dữ', start: 19.85, end: 20.1 },
      { word: 'liệu', start: 20.12, end: 20.4 },
      { word: 'được', start: 20.45, end: 20.7 },
      { word: 'kết', start: 20.72, end: 21.0 },
      { word: 'xuất', start: 21.02, end: 21.35 },
      { word: 'trực', start: 21.4, end: 21.65 },
      { word: 'tiếp.', start: 21.68, end: 22.0 },
    ],
  },
  {
    id: 'cue-5',
    index: 5,
    start: 26.5,
    end: 35.0,
    text: 'Hãy cùng chúng tôi trải nghiệm kỷ nguyên sáng tạo nội dung đa phương tiện đỉnh cao ngay hôm nay!',
    speaker: 'Speaker 1',
    words: [
      { word: 'Hãy', start: 26.5, end: 26.85 },
      { word: 'cùng', start: 26.88, end: 27.2 },
      { word: 'chúng', start: 27.22, end: 27.55 },
      { word: 'tôi', start: 27.58, end: 27.9 },
      { word: 'trải', start: 27.95, end: 28.25 },
      { word: 'nghiệm', start: 28.28, end: 28.6 },
      { word: 'kỷ', start: 28.65, end: 28.95 },
      { word: 'nguyên', start: 28.98, end: 29.35 },
      { word: 'sáng', start: 29.4, end: 29.75 },
      { word: 'tạo.', start: 29.78, end: 30.2 },
    ],
  },
];

export interface SubtitleStudioState {
  audioFile: AudioSourceFile | null;
  model: WhisperModel;
  language: SubtitleLanguage;
  task: SubtitleTask;
  wordTimestamps: boolean;
  jobStatus: 'idle' | 'uploading' | 'transcribing' | 'ready' | 'error';
  jobProgress: number;
  errorMessage: string | null;
  cues: SubtitleCue[];
  detectedLanguage: string;
  activeTab: SubtitleStudioTab;
  searchQuery: string;
  isPlaying: boolean;
  currentTime: number;
  playbackRate: number;
  volume: number;
  isMuted: boolean;
  zoomLevel: number;
  activeCueId: string | null;
  selectedCueId: string | null;
  loopSelectedCue: boolean;
  isShiftModalOpen: boolean;
  isExportModalOpen: boolean;
  exportFormat: SubtitleExportFormat;

  // Actions
  setAudioFile: (file: AudioSourceFile | null) => void;
  loadSampleAudio: () => void;
  setModel: (model: WhisperModel) => void;
  setLanguage: (lang: SubtitleLanguage) => void;
  setTask: (task: SubtitleTask) => void;
  setWordTimestamps: (enabled: boolean) => void;
  startTranscription: () => Promise<void>;
  updateCueText: (id: string, text: string) => void;
  updateCueTiming: (id: string, start: number, end: number) => void;
  splitCue: (id: string, splitAtSec?: number) => void;
  mergeCues: (id1: string, id2: string) => void;
  insertCueAfter: (id: string) => void;
  deleteCue: (id: string) => void;
  shiftTimestamps: (offsetSec: number, scope: 'all' | 'from_current', currentCueId?: string) => void;
  reorderAndReindexCues: () => void;
  setSearchQuery: (q: string) => void;
  setActiveTab: (tab: SubtitleStudioTab) => void;
  play: () => void;
  pause: () => void;
  seek: (timeSec: number) => void;
  setPlaybackRate: (rate: number) => void;
  setVolume: (vol: number) => void;
  toggleMute: () => void;
  setZoomLevel: (zoom: number) => void;
  selectCue: (id: string | null) => void;
  toggleLoopSelectedCue: () => void;
  setShiftModalOpen: (open: boolean) => void;
  setExportModalOpen: (open: boolean) => void;
  setExportFormat: (fmt: SubtitleExportFormat) => void;
  exportDocument: () => string;
  getFilteredCues: () => SubtitleCue[];
  calculateCreditCost: () => number;
}

export const useSubtitleStudioStore = create<SubtitleStudioState>((set, get) => ({
  audioFile: SAMPLE_AUDIO_FILE,
  model: 'whisper-large-v3',
  language: 'auto',
  task: 'transcribe',
  wordTimestamps: true,
  jobStatus: 'ready',
  jobProgress: 100,
  errorMessage: null,
  cues: SAMPLE_SUBTITLE_CUES,
  detectedLanguage: 'vi',
  activeTab: 'editor',
  searchQuery: '',
  isPlaying: false,
  currentTime: 0,
  playbackRate: 1.0,
  volume: 0.85,
  isMuted: false,
  zoomLevel: 1.0,
  activeCueId: 'cue-1',
  selectedCueId: 'cue-1',
  loopSelectedCue: false,
  isShiftModalOpen: false,
  isExportModalOpen: false,
  exportFormat: 'srt',

  setAudioFile: (file) => {
    set({
      audioFile: file,
      jobStatus: file ? 'idle' : 'idle',
      cues: file ? [] : [],
      currentTime: 0,
      isPlaying: false,
      activeCueId: null,
      selectedCueId: null,
    });
  },

  loadSampleAudio: () => {
    set({
      audioFile: SAMPLE_AUDIO_FILE,
      cues: SAMPLE_SUBTITLE_CUES,
      jobStatus: 'ready',
      jobProgress: 100,
      detectedLanguage: 'vi',
      currentTime: 0,
      isPlaying: false,
      activeCueId: 'cue-1',
      selectedCueId: 'cue-1',
    });
  },

  setModel: (model) => set({ model }),
  setLanguage: (language) => set({ language }),
  setTask: (task) => set({ task }),
  setWordTimestamps: (wordTimestamps) => set({ wordTimestamps }),

  calculateCreditCost: () => {
    const { audioFile } = get();
    const duration = audioFile?.durationSec || 30;
    // 1 credit per 30 seconds of audio, rounded up, min 1 credit
    return Math.max(1, Math.ceil(duration / 30));
  },

  startTranscription: async () => {
    const { audioFile } = get();
    if (!audioFile) return;

    set({
      jobStatus: 'uploading',
      jobProgress: 15,
      errorMessage: null,
    });

    // Simulate R2 presigned upload
    await new Promise((res) => setTimeout(res, 400));

    set({
      jobStatus: 'transcribing',
      jobProgress: 45,
    });

    // Simulate Whisper STT inference
    await new Promise((res) => setTimeout(res, 800));

    set({
      jobStatus: 'ready',
      jobProgress: 100,
      cues: SAMPLE_SUBTITLE_CUES,
      detectedLanguage: get().language === 'auto' ? 'vi' : get().language,
      activeCueId: 'cue-1',
      selectedCueId: 'cue-1',
      currentTime: 0,
    });
  },

  updateCueText: (id, text) => {
    set((state) => ({
      cues: state.cues.map((c) => (c.id === id ? { ...c, text } : c)),
    }));
  },

  updateCueTiming: (id, start, end) => {
    set((state) => ({
      cues: state.cues.map((c) =>
        c.id === id
          ? {
              ...c,
              start: Math.max(0, +start.toFixed(3)),
              end: Math.max(start + 0.1, +end.toFixed(3)),
            }
          : c
      ),
    }));
    get().reorderAndReindexCues();
  },

  splitCue: (id, splitAtSec) => {
    const { cues, currentTime } = get();
    const index = cues.findIndex((c) => c.id === id);
    if (index === -1) return;

    const target = cues[index];
    const splitPoint =
      typeof splitAtSec === 'number'
        ? splitAtSec
        : Math.max(target.start + 0.5, Math.min(target.end - 0.5, currentTime));

    if (splitPoint <= target.start || splitPoint >= target.end) return;

    // Split text reasonably by words
    const words = target.text.trim().split(/\s+/);
    const midWordIdx = Math.max(1, Math.floor(words.length / 2));
    const text1 = words.slice(0, midWordIdx).join(' ');
    const text2 = words.slice(midWordIdx).join(' ');

    const cue1: SubtitleCue = {
      ...target,
      end: +splitPoint.toFixed(3),
      text: text1,
    };

    const cue2: SubtitleCue = {
      id: `cue-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      index: target.index + 1,
      start: +splitPoint.toFixed(3),
      end: target.end,
      text: text2,
      speaker: target.speaker,
    };

    const newCues = [...cues.slice(0, index), cue1, cue2, ...cues.slice(index + 1)];
    set({ cues: newCues, selectedCueId: cue1.id });
    get().reorderAndReindexCues();
  },

  mergeCues: (id1, id2) => {
    const { cues } = get();
    const cue1 = cues.find((c) => c.id === id1);
    const cue2 = cues.find((c) => c.id === id2);
    if (!cue1 || !cue2) return;

    const mergedCue: SubtitleCue = {
      id: cue1.id,
      index: cue1.index,
      start: Math.min(cue1.start, cue2.start),
      end: Math.max(cue1.end, cue2.end),
      text: `${cue1.text.trim()} ${cue2.text.trim()}`,
      speaker: cue1.speaker || cue2.speaker,
    };

    const newCues = cues
      .filter((c) => c.id !== id1 && c.id !== id2)
      .concat(mergedCue);

    set({ cues: newCues, selectedCueId: mergedCue.id });
    get().reorderAndReindexCues();
  },

  insertCueAfter: (id) => {
    const { cues } = get();
    const index = cues.findIndex((c) => c.id === id);
    if (index === -1) return;

    const prev = cues[index];
    const newStart = +(prev.end + 0.1).toFixed(3);
    const newEnd = +(newStart + 2.5).toFixed(3);

    const newCue: SubtitleCue = {
      id: `cue-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      index: prev.index + 1,
      start: newStart,
      end: newEnd,
      text: '',
      speaker: prev.speaker,
    };

    const newCues = [...cues.slice(0, index + 1), newCue, ...cues.slice(index + 1)];
    set({ cues: newCues, selectedCueId: newCue.id });
    get().reorderAndReindexCues();
  },

  deleteCue: (id) => {
    const { cues } = get();
    const newCues = cues.filter((c) => c.id !== id);
    set({ cues: newCues });
    get().reorderAndReindexCues();
  },

  shiftTimestamps: (offsetSec, scope, currentCueId) => {
    const { cues } = get();
    if (!offsetSec || cues.length === 0) return;

    const newCues = cues.map((c) => {
      let shouldShift = scope === 'all';
      if (scope === 'from_current' && currentCueId) {
        const currentCue = cues.find((item) => item.id === currentCueId);
        if (currentCue && c.start >= currentCue.start) {
          shouldShift = true;
        }
      }

      if (shouldShift) {
        const newStart = Math.max(0, +(c.start + offsetSec).toFixed(3));
        const newEnd = Math.max(newStart + 0.1, +(c.end + offsetSec).toFixed(3));
        return { ...c, start: newStart, end: newEnd };
      }
      return c;
    });

    set({ cues: newCues, isShiftModalOpen: false });
    get().reorderAndReindexCues();
  },

  reorderAndReindexCues: () => {
    const { cues } = get();
    const sorted = [...cues].sort((a, b) => a.start - b.start);
    const reindexed = sorted.map((c, i) => ({ ...c, index: i + 1 }));
    set({ cues: reindexed });
  },

  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setActiveTab: (activeTab) => set({ activeTab }),

  play: () => set({ isPlaying: true }),
  pause: () => set({ isPlaying: false }),

  seek: (timeSec) => {
    const { cues, loopSelectedCue, selectedCueId } = get();
    const safeTime = Math.max(0, +timeSec.toFixed(3));

    // Handle loop cue logic
    if (loopSelectedCue && selectedCueId) {
      const selectedCue = cues.find((c) => c.id === selectedCueId);
      if (selectedCue && (safeTime < selectedCue.start || safeTime > selectedCue.end)) {
        set({ currentTime: selectedCue.start });
        return;
      }
    }

    // Find active cue at this timestamp
    const active = cues.find((c) => safeTime >= c.start && safeTime <= c.end);

    set({
      currentTime: safeTime,
      activeCueId: active ? active.id : null,
    });
  },

  setPlaybackRate: (playbackRate) => set({ playbackRate }),
  setVolume: (volume) => set({ volume }),
  toggleMute: () => set((state) => ({ isMuted: !state.isMuted })),
  setZoomLevel: (zoomLevel) => set({ zoomLevel }),
  selectCue: (selectedCueId) => set({ selectedCueId }),
  toggleLoopSelectedCue: () => set((state) => ({ loopSelectedCue: !state.loopSelectedCue })),
  setShiftModalOpen: (isShiftModalOpen) => set({ isShiftModalOpen }),
  setExportModalOpen: (isExportModalOpen) => set({ isExportModalOpen }),
  setExportFormat: (exportFormat) => set({ exportFormat }),

  exportDocument: () => {
    const { exportFormat, cues, audioFile, model, detectedLanguage } = get();
    switch (exportFormat) {
      case 'srt':
        return generateSrt(cues);
      case 'vtt':
        return generateVtt(cues);
      case 'txt':
        return generateTxt(cues);
      case 'csv':
        return generateCsv(cues);
      case 'json':
        return generateJson(cues, {
          audioFile: audioFile?.name,
          duration: audioFile?.durationSec,
          model,
          detectedLanguage,
        });
      default:
        return generateSrt(cues);
    }
  },

  getFilteredCues: () => {
    const { cues, searchQuery } = get();
    if (!searchQuery.trim()) return cues;

    const query = searchQuery.toLowerCase().trim();
    return cues.filter(
      (c) =>
        c.text.toLowerCase().includes(query) ||
        (c.speaker && c.speaker.toLowerCase().includes(query)) ||
        String(c.index).includes(query)
    );
  },
}));
